import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/company-logo.png";
import signature from "../../assets/signature.png";

/**
 * Generate Assessment PDF
 * @param {Object} data - Full assessment payload
 * @param {("download"|"view")} [mode="download"] - Mode for PDF output
 */
const generateAssessmentPDF = (data, mode = "download") => {
     const doc = new jsPDF();
     const pageWidth = doc.internal.pageSize.getWidth();

     // ===============================
     //  HEADER SECTION
     // ===============================
     doc.addImage(logo, "PNG", 10, 3, 30, 30);
     doc.setFontSize(10);
     doc.text("VIJAYA DIAGNOSTIC CENTER LIMITED", pageWidth - 10, 15, { align: "right" });
     doc.text("3-6-16 & 17, Street #19, Himayathnagar, Hyderabad -500 029", pageWidth - 10, 20, { align: "right" });
     doc.text("Email: info@vijayadiagnostic.com | Customer Care: 040-2100 0000", pageWidth - 10, 25, { align: "right" });

     doc.setFontSize(14);
     doc.setFont("helvetica", "bold");
     doc.text("RADIOLOGY DOCTOR - INTERVIEW ASSESSMENT SHEET", pageWidth / 2, 40, { align: "center" });

     const candidate = data.candidate || {};

     // ===============================
     // 1️⃣ CANDIDATE INFORMATION
     // ===============================
     const candidateInfo = [
          ["Candidate ID", candidate.id || "-"],
          ["Candidate Name", candidate.candidate_name || "-"],
          ["Position Applied", candidate.position_applied || "-"],
          ["Mobile", candidate.mobile || "-"],
          ["Email", candidate.email || "-"],
     ];

     autoTable(doc, {
          startY: 50,
          head: [["Particulars", "Details"]],
          body: candidateInfo,
          theme: "grid",
          headStyles: { fillColor: [44, 62, 80] },
     });

     // Helper function to avoid cutoffs
     const safeY = (extra = 10) => {
          let y = doc.lastAutoTable?.finalY || 60;
          const pageHeight = doc.internal.pageSize.getHeight();
          if (y + extra > pageHeight - 20) {
               doc.addPage();
               y = 20;
          }
          return y + extra;
     };

     // ===============================
     // 2️⃣ REGIONS
     // ===============================
     if (data.regions?.length) {
          const regionBody = data.regions.map((r, idx) => [idx + 1, r.region_name, r.branch_name]);
          autoTable(doc, {
               startY: safeY(10),
               head: [["#", "Region Name", "Branch Name"]],
               body: regionBody,
               theme: "grid",
               headStyles: { fillColor: [88, 99, 110] },
          });
     }

     // ===============================
     // 3️⃣ MODALITIES & SUBMODALITIES
     // ===============================
     if (data.modalities?.length) {
          const filteredModalities = data.modalities.filter((m) => m.level && m.level.trim() !== "");
          if (filteredModalities.length > 0) {
               const modBody = filteredModalities.map((m, idx) => [
                    idx + 1,
                    m.modality,
                    m.selection_area,
                    m.level,
               ]);
               autoTable(doc, {
                    startY: safeY(10),
                    head: [["#", "Modality", "Item", "Classification"]],
                    body: modBody,
                    theme: "grid",
                    headStyles: { fillColor: [52, 73, 94] },
               });
          }
     }

     // ===============================
     // 4️⃣ PERSONALITY ASSESSMENTS
     // ===============================
     if (data.personality_assessments?.length) {
          const paBody = data.personality_assessments.map((p, idx) => [
               idx + 1,
               p.criteria,
               p.remarks,
          ]);
          autoTable(doc, {
               startY: safeY(10),
               head: [["#", "Criteria", "Remarks"]],
               body: paBody,
               theme: "grid",
               headStyles: { fillColor: [66, 99, 144] },
          });
     }

     // ===============================
     // 5️⃣ TECHNICAL EVALUATIONS
     // ===============================
     if (data.technical_evaluations?.length) {
          const teBody = data.technical_evaluations.map((t, idx) => [
               idx + 1,
               t.particular,
               t.assessment,
               t.notes,
          ]);
          autoTable(doc, {
               startY: safeY(10),
               head: [["#", "Particular", "Assessment", "Notes"]],
               body: teBody,
               theme: "grid",
               headStyles: { fillColor: [99, 66, 77] },
          });
     }

     // ===============================
     // 6️⃣ OVERALL NOTES / RECOMMENDATION
     // ===============================
     if (candidate.overallNotes) {
          const y = safeY(15);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.text("Overall Notes / Recommendation:", 14, y);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          doc.text(candidate.overallNotes, 14, y + 6, { maxWidth: pageWidth - 28 });
     }

     // ===============================
     // 7️⃣ SIGNATURE SECTION
     // ===============================
     let y = safeY(25);
     if (candidate.rpt_mgr_status === "Approved") {
          const pageHeight = doc.internal.pageSize.getHeight();
          if (y + 80 > pageHeight - 20) {
               doc.addPage();
               y = 20;
          }

          // Signature
          doc.addImage(signature, "PNG", 14, y, 30, 15);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(12);
          doc.text(candidate.rm_name || "", 14, y + 20);

          doc.setFontSize(10);
          doc.setFont("helvetica", "italic");
          doc.text("(Director | HOD - Radiology)", 14, y + 25);

          doc.text(
               "I hereby confirm that proper and detailed screening is done for the doctor considering VDCL requirements and accordingly, hiring decision is confirmed.",
               14,
               y + 35,
               { maxWidth: pageWidth - 28 }
          );

          if (candidate.manager_approved_date) {
               doc.text(`Manager Approved Date: ${candidate.manager_approved_date}`, 14, y + 50);
          }
     }

     // ===============================
     // 8️⃣ PAGE NUMBERS
     // ===============================
     const totalPages = doc.internal.getNumberOfPages();
     for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFontSize(9);
          doc.text(
               `Page ${i} of ${totalPages}`,
               pageWidth - 15,
               doc.internal.pageSize.getHeight() - 10,
               { align: "right" }
          );
     }

     // ===============================
     // 9️⃣ DOWNLOAD OR VIEW
     // ===============================
     if (mode === "download") {
          doc.save(`Assessment_${candidate.candidate_name || "Candidate"}.pdf`);
     } else {
          window.open(doc.output("bloburl"));
     }
};

export default generateAssessmentPDF;